import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { cloneDeep } from 'lodash-es';
import { interval, Subscription } from 'rxjs';
import { PAYMENT_CHECK_INTERVAL, PaymentStatus } from '../../../../../domain/common.model';
import { ConfigurationStatus, ConfigurationType } from '../../../../../domain/consts';
import { SalespointDocument } from '../../../../../domain/document.model';
import { FrontierConfiguration } from '../../../../../domain/frontier.domain';
import { Policy } from '../../../../../domain/policy.model';
import {
    CessionPartyType,
    InsuredProductFrontier,
    InsuredProductMotor,
    MotorConfiguration,
    SummaryType,
    VehicleDefinitionMode
} from '../../../../../domain/product.domain';
import { IOfferService } from '../../../service/ioffer.service';
import { IOfferAction } from '../../../store/ioffer.action';
import { IOfferState } from '../../../store/ioffer.state';
import { OfferAction } from '../../../store/offer.action';
import { PolicyAction } from '../../../store/policy.action';

@Component({
    selector: 'app-motor-summary-box',
    templateUrl: './motor-summary-box.component.html',
    styleUrls: ['./motor-summary-box.component.scss']
})

export class MotorSummaryBoxComponent implements OnInit, OnDestroy {

    private subscriptions: Subscription[] = [];

    @Input() configurationType = ConfigurationType.MOTOR;
    @Input() document: SalespointDocument;
    @Input() policyNumber: string = null;
    @Input() readonly = false;
    @Input() type: SummaryType = SummaryType.CALCULATION;
    @Input() fullInsuranceDate = true;
    @Input() iOffer = false;
    @Input() previewEnabled = true;

    inProgress: string[] = [];
    paid: string[] = [];
    vehicleDefinitionMode = VehicleDefinitionMode;
    cessionPartyType = CessionPartyType;
    summaryType = SummaryType;
    configurationTypes = ConfigurationType;

    get hasPayment() {
        return this.iOffer && this.type === SummaryType.POLICY;
    }

    get policy() {
        return this.document as Policy;
    }

    get insuranceScope() {
        const configuration = this.configuration;

        const result =
            this.configurationType === ConfigurationType.MOTOR
                ? [
                    {
                        label: 'MOTOR_CONFIG.INSURANCE_SCOPE_OC',
                        available: (configuration as MotorConfiguration).thirdPartyLiability.selected
                    },
                    {
                        label: 'MOTOR_CONFIG.INSURANCE_SCOPE_AC',
                        available: (configuration as MotorConfiguration).autocasco.selected
                    },
                    {
                        label: 'MOTOR_CONFIG.ADDON_NNW',
                        available: (configuration as MotorConfiguration).accidentInsurance.selected
                    },
                    {
                        label: 'MOTOR_CONFIG.ADDON_ASSSTANCE',
                        available: (configuration as MotorConfiguration).motorAssistance.selected
                    },
                ]
                : [
                    {
                        label: 'FRONTIER_CONFIG.FRONTIER',
                        available: (configuration as FrontierConfiguration).frontierLiability.selected
                    },
                ];
        return result
            .filter(element => element.available)
            .map(element => this.translateService.instant(element.label))
            .join(', ');
    }

    get product() {
        return this.document.productList
            .find(p => p.configuration.configurationType === this.configurationType);
    }


    get configuration() {
        return this.document.productList
            .map(p => p.configuration)
            .find(p => p.configurationType === this.configurationType);
    }

    get hasFinishedConfiguration() {
        if (!this.configuration) {
            return false;
        }
        return this.configuration.configurationStatus === ConfigurationStatus.COMPLETED;
    }

    get printoutLabel() {
        switch (this.type) {
            case SummaryType.CALCULATION:
                return 'LABEL.CALCULATION_PRINTOUT';
            case SummaryType.OFFER:
                return 'LABEL.OFFER_PRINTOUT';
            case SummaryType.POLICY:
                return 'LABEL.POLICY_PRINTOUT';
        }
        return null;
    }

    constructor(private store: Store,
                private iofferService: IOfferService,
                private translateService: TranslateService) {
    }

    ngOnInit(): void {
        this.subscriptions.push(
            this.store.select(IOfferState.getInProgress).subscribe(inProgress => this.inProgress = cloneDeep(inProgress)),
            interval(PAYMENT_CHECK_INTERVAL).subscribe(() => {
                this.inProgress
                    .filter(order => this.paid.indexOf(order) === -1)
                    .forEach(token => this.iofferService.checkPaymentStatus(token)
                        .subscribe(payment => {
                            if (payment.status === PaymentStatus.SUCCESS) {
                                this.paid.push(token);
                            }
                        })
                    );
            })
        );
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(s => s.unsubscribe());
    }


    editCession() {
        const product = this.document.productList
            .find(p => p.configuration.configurationType === this.configurationType);
        if (product) {
            this.store.dispatch(new OfferAction.OpenCessionAside(product));
        }
    }

    deleteCession() {
        const product = this.document.productList
            .find(p => p.configuration.configurationType === this.configurationType);
        this.store.dispatch(new OfferAction.DeleteCession(this.document.id, product.id));
    }

    preview() {
        this.configurationType === ConfigurationType.FRONTIER
            ? this.store.dispatch(new OfferAction.ReadonlyFrontier(this.product as InsuredProductFrontier))
            : this.store.dispatch(new OfferAction.ReadonlyMotor(this.product as InsuredProductMotor));
    }

    download(all) {
        switch (this.type) {
            case SummaryType.CALCULATION:
                this.store.dispatch(new OfferAction.PrintoutCalculation(this.document.id, this.product.id, all));
                break;
            case SummaryType.OFFER:
                this.store.dispatch(
                    this.iOffer
                        ? new IOfferAction.PrintoutOffer(this.document.id, this.product.id, all)
                        : new OfferAction.PrintoutOffer(this.document.id, this.product.id, all)
                );
                break;
            case SummaryType.POLICY:
                this.store.dispatch(
                    this.iOffer
                        ? new IOfferAction.PrintoutPolicy(this.document.id, all)
                        : new PolicyAction.Printout(this.document.id, all)
                );
                break;
        }
    }

    downloadSummary() {
        this.store.dispatch(
            this.iOffer
                ? new IOfferAction.PrintoutSummary(this.document.id)
                : new PolicyAction.PrintoutSummary(this.document.id)
        );
    }

    pay(policy: Policy) {
        this.store.dispatch(new IOfferAction.PayPolicy(policy.orderId));
    }


    toPay(orderId: string) {
        return this.inProgress.indexOf(orderId) === -1 && this.paid.indexOf(orderId) === -1;
    }

    isPending(orderId: string) {
        return this.inProgress.indexOf(orderId) !== -1 && this.paid.indexOf(orderId) === -1;
    }

    isPaid(orderId: string) {
        return this.paid.indexOf(orderId) !== -1;
    }

    downloadCertificate() {
        this.store.dispatch(new PolicyAction.PrintoutFrontierCertificate(this.document.id));
    }
}
